import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Building2, ChartColumn, CreditCard, KeyRound, type LucideIcon, Brain } from "lucide-react";
import { useTranslations } from "use-intl";

import { Card, CardContent, CardHeader, CardTitle } from "@lawline/ui/components/card";
import { Skeleton } from "@lawline/ui/components/skeleton";
import logoIcon from "@lawline/ui/assets/logo-icon.svg";

import { useAuth } from "@platform/auth/context";
import type { PermissionRequest } from "@platform/auth/guards";
import { UsageAreaChart } from "@platform/features/usage/components/usage-area-chart";
import {
	usageSeriesQueryOptions,
	usageSummaryQueryOptions,
} from "@platform/features/usage/usage.queries";
import { formatTokens } from "@platform/features/usage/usage.utils";

// Same routes/labels/permissions as the sidebar (common.nav.items); shown here
// as a centered launcher row into the tenant-scoped pages.
const LINKS: {
	to: "/tenant" | "/memory" | "/api-keys" | "/usage" | "/billing";
	labelKey: "tenant" | "memory" | "apiKeys" | "usage" | "billing";
	icon: LucideIcon;
	permission?: PermissionRequest;
}[] = [
	{ to: "/tenant", labelKey: "tenant", icon: Building2 },
	{
		to: "/memory",
		labelKey: "memory",
		icon: Brain,
		permission: { memory: ["read"] },
	},
	{
		to: "/api-keys",
		labelKey: "apiKeys",
		icon: KeyRound,
		permission: { apiKey: ["read"] },
	},
	{
		to: "/usage",
		labelKey: "usage",
		icon: ChartColumn,
		permission: { usage: ["read"] },
	},
	{
		to: "/billing",
		labelKey: "billing",
		icon: CreditCard,
		permission: { billing: ["manage"] },
	},
];

export function HomePage() {
	const t = useTranslations("home");
	const tStats = useTranslations("usage.summary");
	const tChart = useTranslations("usage.chart");
	const tRequests = useTranslations("usage.requestsChart");
	const tNav = useTranslations("common.nav.items");
	const { can } = useAuth();

	const canUsage = can({ usage: ["read"] });

	// The usage page runs the same billing-period queries, so the cache is shared.
	const summary = useQuery({
		...usageSummaryQueryOptions("billing"),
		enabled: canUsage,
	});
	const series = useQuery({
		...usageSeriesQueryOptions("billing", "day"),
		enabled: canUsage,
	});

	return (
		<section className="mx-auto mt-2 flex w-full max-w-5xl flex-col gap-6">
			<div className="mb-4 flex items-center gap-4">
				<img src={logoIcon} alt="" className="size-9 dark:invert" />
				<h1 className="text-4xl font-semibold">{t("title")}</h1>
			</div>

			<div className="flex flex-col gap-6 sm:flex-row">
				{canUsage && (
					<Card className="flex-1">
						<CardHeader>
							<CardTitle className="text-base">{tRequests("title")}</CardTitle>
						</CardHeader>
						<CardContent>
							{series.isPending ? (
								<Skeleton className="h-[320px] w-full" />
							) : series.error ? (
								<p className="text-destructive flex h-[320px] items-center justify-center text-sm">
									{tChart("error")}
								</p>
							) : series.data.points.length === 0 ? (
								<p className="text-muted-foreground flex h-[320px] items-center justify-center text-sm">
									{tChart("empty")}
								</p>
							) : (
								<UsageAreaChart
									points={series.data.points}
									bucket={series.data.bucket}
									series={[
										{
											key: "requests",
											color: "#3b82f6",
											label: tRequests("series"),
										}, // blue-500
									]}
								/>
							)}
						</CardContent>
					</Card>
				)}

				{/* Right rail: token card + nav links, stacked to fill the chart height. */}
				<div className="flex flex-col justify-between gap-4 sm:w-56">
					{canUsage && (
						<Card>
							<CardHeader>
								<CardTitle className="text-muted-foreground text-sm font-medium">
									{tStats("lawlineTokens")}
								</CardTitle>
							</CardHeader>
							<CardContent>
								{summary.data === undefined ? (
									<Skeleton className="h-8 w-20" />
								) : (
									<p className="font-mono text-2xl font-semibold tabular-nums">
										{formatTokens(summary.data.totals.lawline)}
									</p>
								)}
							</CardContent>
						</Card>
					)}

					{LINKS.filter((l) => !l.permission || can(l.permission)).map((l) => (
						<Link
							key={l.to}
							to={l.to}
							className="bg-card hover:bg-accent flex items-center gap-3 rounded-lg border p-3 text-sm transition-colors"
						>
							<l.icon className="text-muted-foreground size-5 shrink-0" />
							{tNav(l.labelKey)}
						</Link>
					))}
				</div>
			</div>
		</section>
	);
}
